import dagre from "@dagrejs/dagre";
import {
  Background,
  Controls,
  type Edge,
  Handle,
  MiniMap,
  type Node,
  type NodeProps,
  Panel,
  Position,
  ReactFlow,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import { Circle } from "lucide-react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { Tooltip, TooltipContent, TooltipTrigger } from "~/components/ui";
import type { LineageEdge, LineageNodeData } from "~/features/admin/categories/utils/transformers";
import { cn } from "~/lib/utils";

const { Graph } = dagre.graphlib;

const NODE_WIDTH = 480;
const NODE_HEIGHT = 130;

interface CategoryLineageProps {
  nodes: LineageNodeData[];
  edges: LineageEdge[];
  onSelect: (categoryId: string) => void;
  /** When set, the matching node is highlighted as selected. */
  selectedCategoryId?: string | null;
}

interface FlowNodeData extends Record<string, unknown> {
  node: LineageNodeData;
  isSelected: boolean;
}

/** ReactFlow needs us to register custom node types module-side, not inline. */
const nodeTypes = { lineage: LineageNode } as const;

/**
 * Lineage graph for entity / relation categories. xyflow + dagre LR layout.
 * Click a real (non-fake) node to open the Peek panel.
 */
export function CategoryLineage({ nodes, edges, onSelect, selectedCategoryId }: CategoryLineageProps) {
  const { t } = useTranslation();

  const { flowNodes, flowEdges } = useMemo(
    () => layout(nodes, edges, selectedCategoryId ?? null),
    [nodes, edges, selectedCategoryId],
  );

  return (
    <div className="h-[70vh] overflow-hidden rounded-[var(--radius-md)] border border-border-default bg-bg-page">
      <ReactFlow<Node<FlowNodeData>, Edge>
        nodes={flowNodes}
        edges={flowEdges}
        nodeTypes={nodeTypes}
        onNodeClick={(_, node) => {
          const id = node.data.node.categoryId;
          if (id) onSelect(id);
        }}
        panOnScroll
        fitView
        nodesDraggable={false}
        nodesConnectable={false}
        elementsSelectable
        proOptions={{ hideAttribution: true }}
      >
        <Panel position="top-left">
          <div className="inline-flex items-center gap-1.5 rounded-[var(--radius-sm)] border border-border-default bg-white px-2 py-1 text-10-regular text-fg-2">
            <Circle className="h-2 w-2 fill-primary-300 text-primary-300" />
            <span>{t("feature.categories.lineage.graphLegend")}</span>
          </div>
        </Panel>
        <Background gap={20} color="#E5E7EB" />
        <Controls showInteractive={false} />
        <MiniMap
          pannable
          zoomable
          maskColor="rgba(28, 38, 56, 0.06)"
          nodeColor={(n) => ((n.data as FlowNodeData).node.isFake ? "#D1D5DB" : "#5C76A5")}
        />
      </ReactFlow>
    </div>
  );
}

function LineageNode({ data }: NodeProps<Node<FlowNodeData>>) {
  const node = data.node;
  const isSelected = data.isSelected;
  const clickable = !node.isFake;
  const hasDescription = !!node.description;

  const card = (
    <div
      className={cn(
        "flex h-full w-full flex-col justify-center gap-1 rounded-[var(--radius-md)] border bg-white px-4 py-3 text-14-regular shadow-sm transition-colors",
        clickable
          ? "border-border-default text-fg-1 cursor-pointer hover:border-primary-500 hover:bg-primary-50"
          : "border-dashed border-border-strong bg-bg-subtle text-fg-3",
        isSelected && clickable && "border-primary-500 bg-primary-50 ring-2 ring-primary-200",
      )}
    >
      <div className="flex items-center gap-1.5">
        {node.isFake && <Circle className="h-2.5 w-2.5 shrink-0 fill-primary-300 text-primary-300" />}
        <span className="truncate font-semibold">{node.name}</span>
      </div>
      {node.localeLabel && <div className="truncate text-14-regular text-fg-2">{node.localeLabel}</div>}
      <div className="truncate font-mono text-12-regular text-fg-3">{node.path}</div>
    </div>
  );

  return (
    <>
      <Handle type="target" position={Position.Left} className="!h-2 !w-2 !border-none !bg-primary-300" />
      {hasDescription ? (
        <Tooltip>
          <TooltipTrigger asChild>{card}</TooltipTrigger>
          <TooltipContent side="top">{node.description}</TooltipContent>
        </Tooltip>
      ) : (
        card
      )}
      <Handle type="source" position={Position.Right} className="!h-2 !w-2 !border-none !bg-primary-300" />
    </>
  );
}

function layout(
  nodes: readonly LineageNodeData[],
  edges: readonly LineageEdge[],
  selectedCategoryId: string | null,
): { flowNodes: Node<FlowNodeData>[]; flowEdges: Edge[] } {
  const dagreGraph = new Graph().setDefaultEdgeLabel(() => ({}));
  dagreGraph.setGraph({ rankdir: "LR", ranksep: 80, nodesep: 30 });

  for (const n of nodes) dagreGraph.setNode(n.path, { width: NODE_WIDTH, height: NODE_HEIGHT });
  for (const e of edges) dagreGraph.setEdge(e.source, e.target);
  dagre.layout(dagreGraph);

  const flowNodes: Node<FlowNodeData>[] = nodes.map((n) => {
    const pos = dagreGraph.node(n.path);
    return {
      id: n.path,
      type: "lineage",
      data: { node: n, isSelected: selectedCategoryId !== null && n.categoryId === selectedCategoryId },
      position: { x: pos.x - NODE_WIDTH / 2, y: pos.y - NODE_HEIGHT / 2 },
      targetPosition: Position.Left,
      sourcePosition: Position.Right,
      draggable: false,
      selectable: !n.isFake,
      width: NODE_WIDTH,
      height: NODE_HEIGHT,
      style: { padding: 0, background: "transparent", border: "none", boxShadow: "none" },
    };
  });

  const flowEdges: Edge[] = edges.map((e) => ({
    id: e.id,
    source: e.source,
    target: e.target,
    animated: true,
    style: { stroke: "#8DA5C8", strokeWidth: 1.5 },
  }));

  return { flowNodes, flowEdges };
}
